import { tracked } from '@glimmer/tracking';

/**
 * Maintains a reactive set of records seeded by an initial REST fetch and kept
 * up to date by socket INSERT/UPDATE/DELETE messages.
 *
 * This captures the pattern duplicated across the Event, Commentary, Broadcast,
 * and Pixels frontends: subscribe to a socket-guru channel, fetch a seed list
 * over ajax, then apply `{type, id, row}` socket messages to a local table that
 * dedups by an id field. One instance manages one logical table; components that
 * track several record kinds (e.g. start + finish) create one instance each and
 * join them in their own derive step.
 *
 * Beyond the hand-rolled versions it adds: O(1) dedup via a Map, a queue so
 * socket messages arriving mid-fetch are replayed after seeding (closing the
 * race the per-app versions have), and an in-flight guard so a slow async
 * `transform` can't resurrect a record that was deleted while it was resolving.
 *
 * @example
 * // Event – single table, sync filter, sorted in place:
 * this.finish = new SocketRecordTable({
 *   socketGuru: this.socketGuru,
 *   ajax: this.ajax,
 *   channels: 'finishAssociationRecord',
 *   endpoint: () => `/events/${this.args.event.id}/finishAssociationRecords`,
 *   transform: (rec) => rec.eventId === this.args.event.id
 *     ? Object.assign(rec, { color: this.recordColor(rec) })
 *     : null,
 *   sort: this.sortFn,
 * });
 * this.finish.start();           // in constructor
 * // ... this.finish.records in the template
 * this.finish.teardown();        // in willDestroy
 *
 * @example
 * // Commentary / Pixels – async enrich + class-group filter, EmberObject wrap:
 * const makeTable = (channel, kind) => new SocketRecordTable({
 *   socketGuru: this.socketGuru,
 *   ajax: this.ajax,
 *   channels: channel,
 *   endpoint: () => `/events/${eventId}/${kind}`,
 *   transform: async (rec) => {
 *     if (rec.EntryId == null) return null;
 *     const cg = await this.classGroup;
 *     rec.entry = await this.store.findRecord('Entry', rec.EntryId);
 *     const entryClass = await rec.entry.get('ClassId');
 *     if (!entryClass) return null;
 *     const entryClassGroup = await entryClass.get('ClassGroupId');
 *     return entryClassGroup === cg ? EmberObject.create(rec) : null;
 *   },
 *   onChange: () => this.updateResults(),
 * });
 */
export default class SocketRecordTable {
	/**
	 * The reactive table contents. Reassigned on every change so templates and
	 * derived getters track it. Sorted when a `sort` comparator was supplied.
	 * @type {Array}
	 */
	@tracked records = [];

	/**
	 * @param {Object}   options
	 * @param {Object}   options.socketGuru  Service exposing an `events` EventTarget that
	 *                                        dispatches `newEvent` with `{ detail: { eventName, data } }`.
	 * @param {Object}   options.ajax         Service with `request(url) => Promise<Array>` for the seed fetch.
	 * @param {String|String[]} options.channels  Socket event name(s) this table listens to.
	 * @param {String|Function} [options.endpoint]  Seed URL, or a function returning it. Omit for socket-only tables.
	 * @param {String}   [options.idField='id']  Record field used as the dedup/delete key.
	 * @param {Function} [options.transform]   `(record) => record | null | Promise<record|null>`.
	 *                                          Enrich, filter, and/or wrap an incoming record; return `null`
	 *                                          to drop it (a dropped UPDATE also removes any existing copy).
	 *                                          Default: identity.
	 * @param {Function} [options.sort]        Comparator; when set, `records` is kept sorted.
	 * @param {Function} [options.decode]      `(eventName, payload) => { op, id, record }`. Override the
	 *                                          message format. Default parses the `{type, id, row}` shape.
	 * @param {Function} [options.onChange]    Called with `this` after every committed mutation.
	 */
	constructor({
		socketGuru,
		ajax,
		channels,
		endpoint = null,
		idField = 'id',
		transform = null,
		sort = null,
		decode = null,
		onChange = null,
	} = {}) {
		this.socketGuru = socketGuru;
		this.ajax = ajax;
		this._channels = Array.isArray(channels) ? channels : [channels];
		this.endpoint = endpoint;
		this.idField = idField;
		this.transform = transform;
		this.sort = sort;
		this.decode = decode || this._defaultDecode.bind(this);
		this.onChange = onChange;

		// id -> stored record. Map preserves insertion order for the unsorted case.
		this._map = new Map();
		// Socket messages that arrive while the seed fetch is in flight.
		this._queue = [];
		this._fetching = false;
		// Monotonic op counter + latest op seq per id, used to drop stale async upserts.
		this._opSeq = 0;
		this._latestOp = new Map();
		this._listener = null;
	}

	/**
	 * Subscribe to the socket and run the initial fetch.
	 * @returns {Promise} resolves when the seed fetch (and queued replay) completes.
	 */
	start() {
		if (!this._listener) {
			this._listener = this._handleSocketEvent.bind(this);
			this.socketGuru.events.addEventListener('newEvent', this._listener);
		}
		return this.refetch();
	}

	/**
	 * Remove the socket subscription. Call from the consumer's `willDestroy`.
	 */
	teardown() {
		if (this._listener) {
			this.socketGuru.events.removeEventListener('newEvent', this._listener);
			this._listener = null;
		}
	}

	/**
	 * Clear the table and re-seed from the endpoint. Socket messages that arrive
	 * during the fetch are queued and replayed once seeding finishes.
	 * @returns {Promise}
	 */
	async refetch() {
		this._fetching = true;
		this._map.clear();
		this._queue = [];

		try {
			const url = typeof this.endpoint === 'function' ? this.endpoint() : this.endpoint;
			if (url) {
				const records = await this.ajax.request(url);
				await Promise.all((records || []).map((record) => this._upsert(record)));
			}
		} finally {
			this._fetching = false;
		}

		// Replay anything that arrived mid-fetch, in arrival order.
		const queued = this._queue;
		this._queue = [];
		for (const decoded of queued) {
			await this._applyDecoded(decoded);
		}

		this._commit();
	}

	_handleSocketEvent(event) {
		const { eventName, data } = event.detail;
		if (!this._channels.includes(eventName)) {
			return;
		}

		let decoded;
		try {
			decoded = this.decode(eventName, data);
		} catch (e) {
			console.error('[SocketRecordTable] failed to decode message', e, data);
			return;
		}
		if (!decoded || !decoded.op) {
			return;
		}

		if (this._fetching) {
			this._queue.push(decoded);
			return;
		}
		this._apply(decoded);
	}

	/** Default decoder for the socket-guru `{type, id, row}` envelope. */
	_defaultDecode(eventName, payload) {
		const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
		const decoded = { op: data.type, id: data.id, record: null };
		if (data.type === 'INSERT' || data.type === 'UPDATE') {
			decoded.record = typeof data.row === 'string' ? JSON.parse(data.row) : data.row;
		}
		return decoded;
	}

	async _apply(decoded) {
		await this._applyDecoded(decoded);
		this._commit();
	}

	async _applyDecoded(decoded) {
		if (decoded.op === 'DELETE') {
			this._removeById(decoded.id);
		} else {
			// INSERT and UPDATE both upsert; the Map dedups by id.
			await this._upsert(decoded.record);
		}
	}

	async _upsert(raw) {
		if (raw == null) {
			return;
		}
		const key = raw[this.idField];
		// Claim the latest-op slot for this id before awaiting the transform.
		const seq = ++this._opSeq;
		this._latestOp.set(key, seq);

		let result;
		try {
			result = this.transform ? await this.transform(raw) : raw;
		} catch (e) {
			console.error('[SocketRecordTable] transform failed', e, raw);
			return;
		}

		// A newer op (delete, or a more recent update) landed while we awaited.
		if (this._latestOp.get(key) !== seq) {
			return;
		}
		if (result == null) {
			// Filtered out — also drop any existing copy (an UPDATE that no longer matches).
			this._map.delete(key);
		} else {
			this._map.set(key, result);
		}
	}

	/**
	 * Replace every stored record matching `predicate` with `updater(record)`,
	 * then re-commit. For cross-cutting changes that aren't keyed by `idField` —
	 * e.g. a status broadcast that recolours every row for a given entry.
	 * `updater` should preserve the record's `idField` value.
	 * @param {Function} predicate  `(record) => boolean`
	 * @param {Function} updater    `(record) => record`
	 */
	updateWhere(predicate, updater) {
		let changed = false;
		for (const [key, record] of this._map) {
			if (predicate(record)) {
				this._map.set(key, updater(record));
				changed = true;
			}
		}
		if (changed) {
			this._commit();
		}
	}

	_removeById(id) {
		// Bump the op counter so an in-flight upsert for this id is discarded.
		this._latestOp.set(id, ++this._opSeq);
		this._map.delete(id);
	}

	_commit() {
		let next = [...this._map.values()];
		if (this.sort) {
			next.sort(this.sort);
		}
		this.records = next;
		if (this.onChange) {
			this.onChange(this);
		}
	}
}
